<template>
	<ProductPage
		:parametrics="parametrics"
		:image-gallery="imageGallery"
		:product-attributes="productAttributes"
		:product-details="productDetails"
		:color-from-parametrics-in-link="colorFromParametricsInLink"
		:special-price="specialPrice"
		:product-recommendations="productRecommendations"
		:size-guide-tab="sizeGuideTab"
		:washing-instructions-tab="washingInstructionsTab"
	/>
</template>

<script setup lang="ts">
import {
	buildMetaDescriptionFromSeoField,
	buildProductSchemaDescription
} from "~/layers/base/utils/productMetaDescription";
import {
	buildProductMetaKeywords,
	buildProductMetaTitle
} from "~/layers/base/utils/productSeoMeta";
import type {
	GroupedParametrics,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import type {
	BlokGenericForComponents,
	BlokSEOApp,
	BlokSizeGuideTab,
	BlokWashingInstructionsTab,
	SpecialPrice
} from "~/types/storyblok/storyblok";
import { getLowestPriceIncVat } from "~/utils/product/getLowestPriceIncVat";
import { historicalBestPricesParams } from "~/utils/product/historicalBestPricesParams";
import { filterInactiveColorParametrics } from "~/utils/product/filterInactiveColorParametrics";
import { activeStatusID } from "~/utils/constants";
import { buildProductBrand } from "~/layers/base/domain/product/product-brand";
import { buildOfferDates } from "~/layers/base/domain/product/product-offer-dates";
import type { BreadcrumbsCategory } from "~/types/composables/useCategoryTypes";

const props = defineProps<{
	localizedSeo?: Record<string, { name?: string; url?: string }>;
}>();
// [...slug].vue also passes `slug` / `unique-product-name`, which this page
// doesn't use; the root is a fragment (breadcrumbs + content), so they can't
// fall through and Vue warns. Drop them instead.
defineOptions({ inheritAttrs: false });

const {
	getProductByUniqueName,
	getProductParametrics,
	selectProductVariant,
	setColorParametricsInStock,
	getSecondParametricName,
	listHistoricalBestPrices,
	getProductRelations,
	listIncludedProducts,
	getProductStock,
	listProductPriceQtyBreak
} = useNorcePwa();

const { fetchStory } = useStoryblokFetcher();
const config = useRuntimeConfig();
const { setPageTitle, setPageType, setCategoryBreadcrumb } = usePageStore();
const { resolveProductCategoryBreadcrumb } = useProductCategoryBreadcrumb();
const { buildBreadcrumbItems } = useCategoryBreadcrumb();
const productStore = useProductStore();
const route = useRoute();
const requestURL = useRequestURL();

const parametrics = ref<GroupedParametrics>();
const { storeContext, currentCurrency } = storeToRefs(useNorceStore());
// At setup on purpose — it captures the Nuxt instance the handler loses.
const { showPageNotFound } = usePageNotFound();
const { fetchAllParametrics, ensureParametricsForProduct } =
	useAllParametrics();
const { fetchProductTypes, getProductType } = useProductTypes();
const { locale, locales } = useI18n();
const localePath = useLocalePath();
const { stores } = storeToRefs(useWarehousesAndStores());
const appStore = useAppStore();
const storyblokLanguage = useStoryblokLanguage();

const imageGallery = ref<BlokGenericForComponents[]>();
const productAttributes = ref<BlokGenericForComponents[]>();
const productDetails = ref<BlokGenericForComponents[]>();
const specialPrice = ref<SpecialPrice | null>(null);
const seo = ref<BlokSEOApp>();
const colorFromParametricsInLink = ref<ParametricListValue>();
const productRecommendations = ref<BlokGenericForComponents[]>();
const sizeGuideTab = ref<BlokSizeGuideTab | undefined>();
const washingInstructionsTab = ref<BlokWashingInstructionsTab | undefined>();
const productCategoryBreadcrumb = ref<BreadcrumbsCategory | null>(null);

const getProduct = async () => {
	const { data } = await fetchStory("layouts/product-page", {
		version: config.public.storyblokVersion,
		resolve_links: "url",
		language: storyblokLanguage.value
	});

	imageGallery.value = data.story.content.body[0].image_gallery;
	productAttributes.value = data.story.content.body[0].product_attributes;
	productDetails.value = data.story.content.body[0].product_details;
	seo.value = data.story.content.SEO;
	specialPrice.value = {
		background:
			data.story.content.body[0].special_price_background_color?.value || "",
		text: data.story.content.body[0].special_price_text_color?.value || "",
		enabled: !!data.story.content.body[0].show_special_price_end_date
	};
	productRecommendations.value =
		data.story.content.body[0].product_recommendations;
	const productDetailsTabs =
		data.story.content.body[0].product_details?.[0]?.tabs;
	if (productDetailsTabs) {
		sizeGuideTab.value = productDetailsTabs.find(
			(tab: { component: string }) => tab.component === "size_guide_tab"
		) as BlokSizeGuideTab | undefined;
		washingInstructionsTab.value = productDetailsTabs.find(
			(tab: { component: string }) =>
				tab.component === "washing_instructions_tab"
		) as BlokWashingInstructionsTab | undefined;
	}
};
const fetchProduct = async () => {
	const storesIds = stores?.value?.[locale.value]?.map((store) => store.Id);
	const [productsFetched, parametricsFetched] = await Promise.all([
		getProductByUniqueName({
			uniqueName: route.params.uniqueProductName
				? (route.params.uniqueProductName as string)
				: (route.params.slug as string),
			...(storesIds?.length && { storeSeed: storesIds.toString() }),
			...storeContext.value,
			statusSeed: String(activeStatusID)
		}),
		fetchAllParametrics(),
		fetchProductTypes(),
		getProduct()
	]);

	// Only needs the product's CategoryId — start it now, alongside the
	// parametric / price / bundle calls below, instead of after them.
	const categoryBreadcrumbPromise = resolveProductCategoryBreadcrumb(
		productsFetched.CategoryId,
		locale.value
	);

	const allParametricsForProduct = await ensureParametricsForProduct(
		productsFetched,
		parametricsFetched
	);
	const productParametrics = await getProductParametrics(
		{
			...storeContext.value
		},
		{ product: productsFetched, allParametrics: allParametricsForProduct }
	);
	if (productParametrics.color) {
		productParametrics.color = setColorParametricsInStock({
			parametrics: productParametrics,
			products: productsFetched
		});
		productParametrics.color = filterInactiveColorParametrics({
			parametrics: productParametrics,
			products: productsFetched
		});
	}

	parametrics.value = productParametrics;
	let selectedColorLocal: ParametricListValue | undefined,
		selectedSecondParametricLocal: ParametricListValue | undefined;

	const selectedPartNo = productsFetched.PartNo;
	const selectedVariant = productsFetched.Variants?.find(
		(variant) => variant.PartNo === selectedPartNo
	);
	if (selectedVariant) {
		const selectedColorId = selectedVariant.VariantParametrics?.find(
			(parametric) => parametric.Code === "color"
		)?.ValueId;
		if (selectedColorId) {
			selectedColorLocal = productParametrics.color?.find(
				(parametric) => parametric.Id === selectedColorId
			);
		}
		const selectedSecondParametricId = selectedVariant.VariantParametrics?.find(
			(parametric) =>
				parametric.Code === getSecondParametricName(productParametrics)
		)?.ValueId;
		if (selectedSecondParametricId) {
			const secondParams =
				productParametrics[getSecondParametricName(productParametrics)];
			if (secondParams) {
				selectedSecondParametricLocal = secondParams.find(
					(parametric) => parametric.Id === selectedSecondParametricId
				);
			}
		}
	}

	if (!selectedColorLocal && productParametrics.color?.length) {
		selectedColorLocal = productParametrics.color[0];
	}

	productStore.addSelectedColor(selectedColorLocal);

	if (selectedSecondParametricLocal) {
		productStore.addSelectedSecondParametric(selectedSecondParametricLocal);
	} else {
		productStore.addSelectedSecondParametric(undefined);
	}

	productStore.addProducts(productsFetched);
	productStore.addParametrics(productParametrics);

	const selectedProduct = selectProductVariant({
		selectedColorId: selectedColorLocal?.Id,
		selectedSecondParametricId: selectedSecondParametricLocal?.Id,
		products: productsFetched,
		parametricsAvailable:
			productParametrics?.color ??
			productParametrics[getSecondParametricName(productParametrics)]
	});

	// `selectProductVariant` is typed `Product` but returns `undefined` when the
	// URL asks for a color/size pair no variant has.
	if (!selectedProduct?.Id) {
		showPageNotFound();
		return;
	}

	const productType = getProductType(selectedProduct.Type);

	productStore.addSelectedProduct(selectedProduct);
	productStore.setProductType(productType);

	if (selectedProduct.HasQuantityBreaks) {
		if (!(selectedProduct.Id in productStore.quantityBreaksByVariant)) {
			try {
				const qtyBreakData = await listProductPriceQtyBreak({
					id: selectedProduct.Id,
					...storeContext.value
				});
				if (Array.isArray(qtyBreakData)) {
					productStore.setQuantityBreaksForVariant(
						selectedProduct.Id,
						qtyBreakData
					);
				}
			} catch (error) {
				console.error("Error loading listProductPriceQtyBreak:", error);
				productStore.setQuantityBreaksForVariant(selectedProduct.Id, []);
			}
		}
	} else {
		productStore.setQuantityBreaksForVariant(selectedProduct.Id, []);
	}

	if (productType === ProductTypes.BUNDLE) {
		const includedProducts = await listIncludedProducts({
			partNo: selectedProduct.PartNo
		});
		productStore.addBundleProducts(includedProducts?.Items || []);
	} else {
		productStore.addBundleProducts([]);
	}

	try {
		const historicalBestPrices = await listHistoricalBestPrices(
			historicalBestPricesParams(selectedProduct.PartNo, storeContext.value)
		);
		const historicalLowestPrice = getLowestPriceIncVat(
			selectedProduct.PriceIncVat,
			historicalBestPrices
		);
		productStore.addLowestPrice(historicalLowestPrice);
	} catch {
		productStore.addLowestPrice(null);
	}

	productCategoryBreadcrumb.value = await categoryBreadcrumbPromise;

	// set page title, type and breadcrumb to pageStore
	setPageTitle(selectedProduct.VariantName ?? selectedProduct.Name);
	setPageType("product");
	setCategoryBreadcrumb(productCategoryBreadcrumb.value);
};

const selectedProductId = computed(() => productStore.selectedProduct?.Id);

const storeSeed = computed(
	() => stores.value?.[locale.value]?.map((store) => store.Id).toString() ?? ""
);

/**
 * What the product request actually depends on, as a string.
 *
 * Replaces the old `watch(stores)` / `watch(storeContext)` pair. `storeContext`
 * is a getter that returns a NEW object on every evaluation, and
 * `useAsyncData`'s `watch` is a plain (non-deep) watch — so watching it directly
 * re-triggered on identity alone, refetching the whole product at hydration.
 */
const requestKey = computed(() =>
	JSON.stringify({ ...storeContext.value, storeSeed: storeSeed.value })
);

/**
 * The page used to `await fetchProduct()` (plus the relations block) straight in
 * setup, so hydration repeated all of it in the browser: the product, the
 * historical prices and the relations. Through `useAsyncData` the server result
 * travels in the Nuxt payload instead.
 *
 * `fetchProduct` is untouched — it still writes productStore and the local refs.
 * The snapshot exists because the handler does NOT run on the client when the
 * payload is reused, which would otherwise leave those refs (gallery blocks,
 * special price, parametrics…) empty.
 */
const captureSnapshot = () => ({
	parametrics: parametrics.value,
	colorFromParametricsInLink: colorFromParametricsInLink.value,
	imageGallery: imageGallery.value,
	productAttributes: productAttributes.value,
	productDetails: productDetails.value,
	productRecommendations: productRecommendations.value,
	specialPrice: specialPrice.value,
	seo: seo.value,
	sizeGuideTab: sizeGuideTab.value,
	washingInstructionsTab: washingInstructionsTab.value,
	categoryBreadcrumb: productCategoryBreadcrumb.value,
	pageTitle:
		productStore.selectedProduct?.VariantName ??
		productStore.selectedProduct?.Name ??
		""
});

const applySnapshot = (snapshot: ReturnType<typeof captureSnapshot>) => {
	parametrics.value = snapshot.parametrics;
	colorFromParametricsInLink.value = snapshot.colorFromParametricsInLink;
	imageGallery.value = snapshot.imageGallery;
	productAttributes.value = snapshot.productAttributes;
	productDetails.value = snapshot.productDetails;
	productRecommendations.value = snapshot.productRecommendations;
	specialPrice.value = snapshot.specialPrice;
	seo.value = snapshot.seo;
	sizeGuideTab.value = snapshot.sizeGuideTab;
	washingInstructionsTab.value = snapshot.washingInstructionsTab;
	productCategoryBreadcrumb.value = snapshot.categoryBreadcrumb ?? null;
	// A reused payload skips fetchProduct, so the store would keep the crumb,
	// title and page type of the previously visited page — LayoutBreadcrumbs
	// reads all three.
	setCategoryBreadcrumb(productCategoryBreadcrumb.value);
	if (snapshot.pageTitle) {
		setPageTitle(snapshot.pageTitle);
		setPageType("product");
	}
};

const { data: productSnapshot } = await useAsyncData(
	// The price context belongs in the key: the sales area and the shopper's own
	// price lists both live in the browser, the server renders neither, and a
	// matching key makes the browser keep the payload the server sent. See
	// `priceContextKey` in the norce store.
	`product-page:${route.params.uniqueProductName ?? route.params.slug}:${
		locale.value
	}:${useNorceStore().priceContextKey}`,
	async () => {
		await fetchProduct();

		const productId = productStore.selectedProduct?.Id;
		productStore.addRelations(
			productId
				? await getProductRelations({
						productId,
						...storeContext.value
					})
				: []
		);

		return captureSnapshot();
	},
	{ watch: [requestKey] }
);

watch(
	productSnapshot,
	(snapshot) => {
		if (snapshot) applySnapshot(snapshot);
	},
	{ immediate: true }
);

const seoInfo = computed(() => {
	const product = productStore.selectedProduct || useProductStore().products;

	if (!product) {
		return {
			title: "",
			metaTitle: "",
			ogTitle: "",
			description: "",
			keywords: "",
			ogImage: ""
		};
	}

	return {
		title: `${product.VariantName || product.Name || ""}`,
		// The SEO fields may sit on the parent only, not on each variant.
		metaTitle: buildProductMetaTitle(product, productStore.products),
		ogTitle: product.VariantName || product.Name,
		keywords: buildProductMetaKeywords(product, productStore.products),
		// The Norce SEO field (variant, then parent) first, then this template's
		// own chain.
		description:
			buildMetaDescriptionFromSeoField(
				product.MetaDescription || productStore.products?.MetaDescription,
				product.SubHeader,
				product.Description
			) ||
			product.VariantName ||
			product.Name,
		images: product.Files?.map((image) => image.Path) || [],
		ogImage: product.Files?.[0]?.Path || "",
		sku: product.PartNo,
		price: product.PriceIncVat,
		inStock: getProductStock(product) > 0
	};
});
const domain = computed(() => requestURL.origin);
const parentUniqueName = computed(() => productStore.products?.UniqueName);

// Prefer Meilisearch seo from props; fall back to parentUniqueName × locales
// for Meilisearch misses (stale feed, new products).
const canonicalUrlByIso = computed(() => {
	if (props.localizedSeo) {
		const out: Record<string, string> = {};
		for (const [iso, entry] of Object.entries(props.localizedSeo)) {
			if (entry?.url) out[iso] = entry.url;
		}
		if (Object.keys(out).length > 0) return out;
	}

	const name = parentUniqueName.value;
	if (!name) return undefined;

	return Object.fromEntries(
		locales.value
			.map((l) => [l.iso, name] as const)
			.filter(([iso]) => typeof iso === "string")
	);
});

useLocalizedCanonical(canonicalUrlByIso);

if (import.meta.server) {
	// Any found product gets a card; without an image it is text-only.
	if (seoInfo.value?.title) {
		defineOgImageComponent("OgProduct", {
			image: seoInfo.value.ogImage || undefined,
			logo: appStore.siteLogo?.filename || "",
			title: seoInfo.value.ogTitle || "",
			description: seoInfo.value.description || "",
			width: 1200,
			height: 630
		});
	}
}
useSeoMeta({
	title: () => seoInfo.value.metaTitle || "",
	ogTitle: () => seoInfo.value.ogTitle || seoInfo.value.title || "",
	description: () => seoInfo.value.description || "",
	ogDescription: () => seoInfo.value.description || "",
	keywords: () => seoInfo.value.keywords || undefined,
	ogType: "product",
	// og:url must match canonical — always the parent URL, not the variant slug.
	ogUrl: () =>
		parentUniqueName.value
			? `${domain.value}${localePath("/" + parentUniqueName.value)}`
			: `${domain.value}${route.path}`
});

// Home / category chain / product — same path the visible breadcrumbs show.
const buildProductBreadcrumbItems = (productUrl: string) => {
	const leadingItems = buildBreadcrumbItems(productCategoryBreadcrumb.value);
	return [
		...leadingItems,
		{
			position: leadingItems.length + 1,
			name: seoInfo.value.title || "",
			item: productUrl
		}
	];
};

const siteConfig = useSiteConfig();
const productBrand = computed(() => {
	const product = productStore.selectedProduct || productStore.products;
	return buildProductBrand(product?.Manufacturer?.Name, siteConfig.name);
});

useSchemaOrg(
	computed(() => {
		// Schema.org URLs must match canonical — use parent URL, not the variant slug.
		const productUrl = parentUniqueName.value
			? `${domain.value}${localePath("/" + parentUniqueName.value)}`
			: `${domain.value}${route.path}`;

		return [
			defineProduct({
				name: seoInfo.value.title || "",
				description:
					// The selected variant first, then the parent's text.
					buildProductSchemaDescription(productStore.selectedProduct) ||
					buildProductSchemaDescription(useProductStore().products) ||
					false,
				sku: seoInfo.value.sku || "",
				...(productBrand.value ? { brand: productBrand.value } : {}),
				image: seoInfo.value.images || [],
				offers: {
					"@type": "Offer",
					url: productUrl,
					priceCurrency: currentCurrency.value?.Code || "",
					price: seoInfo.value.price?.toString() || "",
					// Same object seoInfo takes the price from.
					...buildOfferDates(
						(productStore.selectedProduct || productStore.products)?.Updated
					),
					availability: seoInfo.value.inStock
						? "https://schema.org/InStock"
						: "https://schema.org/OutOfStock",
					itemCondition: "https://schema.org/NewCondition",
					seller: {
						"@type": "Organization",
						name: appStore.applicationNorce?.Name || ""
					}
				}
			}),
			defineBreadcrumb({
				itemListElement: buildProductBreadcrumbItems(productUrl)
			})
		];
	})
);

// `stores` / `storeContext` are covered by `requestKey` in the useAsyncData
// call above — watching them here refetched the product on every hydration.

watch(selectedProductId, async () => {
	const product = productStore.selectedProduct || useProductStore().products;

	if (product.HasQuantityBreaks) {
		if (!(product.Id in productStore.quantityBreaksByVariant)) {
			try {
				const qtyBreakData = await listProductPriceQtyBreak({
					id: product.Id,
					...storeContext.value
				});
				if (Array.isArray(qtyBreakData)) {
					productStore.setQuantityBreaksForVariant(product.Id, qtyBreakData);
				}
			} catch (error) {
				console.error("Error loading listProductPriceQtyBreak:", error);
				productStore.setQuantityBreaksForVariant(product.Id, []);
			}
		}
	} else {
		productStore.setQuantityBreaksForVariant(product.Id, []);
	}
});
</script>
